import React, {useEffect, useRef, useState} from "react";
import "./Restore.css";
import Row from "react-bootstrap/Row";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import {ProgressBar} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBolt, faCloudArrowDown, faDownload, faFolderOpen, faHome, faMagnifyingGlass, faSpinner
} from "@fortawesome/free-solid-svg-icons";
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory, {textFilter} from 'react-bootstrap-table2-filter';
import {sleep} from "../../../../lib/utils";
import Badge from "react-bootstrap/Badge";
import {faTrashCan} from "@fortawesome/free-regular-svg-icons";
import {confirm} from "../../../Partials/ConfirmDialog/ConfirmDialog";
import config from "../../../../config";


function Restore({bond, backupApi, restoreApi, updatedFolder}) {
    const [restoreStarted, setRestoreStarted] = useState(false);
    const [restoreStatus, setRestoreStatus] = useState("");
    const [restoreProgress, setRestoreProgress] = useState(0);

    const [isSearching, setIsSearching] = useState(false);
    const searchBackupRef = useRef();
    const [filesList, setFilesList] = useState([]);

    useEffect(() => {
        searchBackupRef.current.value = bond.id;
    }, [bond]);

    useEffect(() => {
        if (updatedFolder) {
            searchBackupRef.current.value = updatedFolder;
            getBackupsList();
        }
    }, [updatedFolder]);


    async function deleteBackup(folder, filename) {
        const confirmDelete = await confirm("Are you sure you want to delete this backup?", "Delete", "Cancel", "Delete Backup");
        if (confirmDelete) {
            try {
                await restoreApi.deleteBackup(folder, filename);
                setFilesList(filesList.filter(file => file.filename !== filename));
            } catch (e) {
                console.error(e);
            }
        }
    }

    function renderBackupOptions(cellContent, row) {
        if (row.filename.indexOf(".") > -1) {
            let backupFileUrl = `${restoreApi.apiUrl}/palantir/backup/${searchBackupRef.current.value}/${row.filename}`;
            return <div className={"restoreBackupOptions"}>
                <Button className="me-1" href={backupFileUrl}><FontAwesomeIcon icon={faDownload}/></Button>
                <Button className="me-1" onClick={() => {
                    startRestore(searchBackupRef.current.value, row.filename);
                }} disabled={restoreStarted}><FontAwesomeIcon icon={faBolt}/></Button>
                <Button className="me-1" variant={"danger"} onClick={() => {
                    deleteBackup(searchBackupRef.current.value, row.filename);
                }}><FontAwesomeIcon icon={faTrashCan}/></Button>
            </div>
        } else {
            return <Button className="me-1" onClick={() => {
                searchBackupRef.current.value = row.filename;
                getBackupsList();
            }}>
                <FontAwesomeIcon icon={faFolderOpen}/>
            </Button>
        }
    }

    function renderBackupFilename(cellContent, row) {
        if (cellContent.indexOf(".") > -1) {
            return <div>
                <Form.Control as={"input"} value={cellContent} readOnly={true}/>
            </div>
        } else {
            return <div>
                <Form.Control as={"input"} value={cellContent + "/"} readOnly={true}/>
            </div>
        }
    }

    function getBackupLabel(filename) {
        return localStorage.getItem(`backup-label-${filename}`);
    }

    function setBackupLabel(filename, label) {
        return localStorage.setItem(`backup-label-${filename}`, label);
    }

    function renderLabel(cellContent, row) {
        return <InputGroup>
            <Form.Control as="textarea" onChange={(e) => {
                setBackupLabel(row.filename, e.target.value)
            }} defaultValue={getBackupLabel(row.filename)}/>
        </InputGroup>
    }

    function renderBackupLastModified(cellContent, row) {
        if (cellContent) {
            let dateFormatted = new Date(cellContent).toLocaleString('en-US', {
                month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false
            })
            return <h4><Badge bg={"secondary"}>{dateFormatted}</Badge></h4>
        }
        return "-";
    }

    const backupListHeaders = [{
        dataField: 'x', text: <Button className="me-1" variant={"light"} onClick={() => {
            searchBackupRef.current.value = "";
            getBackupsList();
        }}>
            <FontAwesomeIcon icon={faHome}/>
        </Button>, formatter: renderBackupOptions
    }, {
        dataField: 'filename',
        text: 'Backup Filename',
        sort: true,
        filter: textFilter({placeholder: "filter", caseSensitive: false, className: "ms-2", delay: 50}),
        formatter: renderBackupFilename
    }, {
        dataField: '', text: 'Label', formatter: renderLabel
    }, {
        dataField: 'last_modified', text: 'Last Modified', sort: true, formatter: renderBackupLastModified
    }]

    async function startRestore(sourceId, filename) {
        const confirmRestore = await confirm(`Are you sure you want to restore this backup in bond ${bond.id}?`, "Restore", "Cancel", "Restore Backup");
        if (!confirmRestore) return;

        setRestoreStatus("Started")
        setRestoreStarted(true);
        setRestoreProgress(2);
        const restoreResponse = await bond.put("/sys/backup", {
            "host": config.backup.hostIp || backupApi.host,
            "http_port": `${config.backup.hostPort || backupApi.port}`,
            "restore": 1,
            "path": `/backup/${sourceId}`,
            "filename": filename
        });
        if (restoreResponse && !restoreResponse.ok) {
            const detail = restoreResponse.status === 405
                ? 'Write access is disabled for this proxy (405 Method Not Allowed).'
                : `Server returned ${restoreResponse.status}`;
            setRestoreStatus(detail);
            setRestoreStarted(false);
            setRestoreProgress(-1);
            return;
        }
        setRestoreProgress(Math.floor(4 + Math.random() * 10));
        await sleep(500);
        await waitRestore();
    }

    async function waitRestore() {
        let response = await bond.get("/sys/backup");
        setRestoreStatus("Restoring")

        if (!response) {
            await sleep(500);
            response = await bond.get("/sys/backup");
            if (!response) {
                setRestoreStarted(false);
                setRestoreProgress(-1);
                return;
            }
        }

        if (response["progress"] === 1000) {
            setRestoreProgress(90 + Math.floor(Math.random() * 8));
            await waitReboot();
        } else if (response["progress"] === -1) {
            setRestoreStarted(false);
            setRestoreProgress(-1);
        } else {
            if (response["progress"]) setRestoreProgress(response["progress"] / 10);
            setTimeout(waitRestore, 300);
        }
    }

    async function waitReboot() {
        setRestoreStatus("Rebooting")
        await bond.put('/sys/reboot', {})
        await sleep(5000);
        while (true) {
            try {
                let response = await bond.get('/sys/version');
                if (!("fw_ver" in response)) throw new Error("Rebooting");
                setRestoreStarted(false);
                setRestoreStatus("Done!")
                return;
            } catch {
                await sleep(2000);
            }
        }
    }

    async function getBackupsList() {
        setRestoreStarted(false);
        setRestoreProgress(0);
        setIsSearching(true);
        setFilesList([]);
        const response = await restoreApi.getBackupsList(searchBackupRef.current.value);
        if (response.files) {
            setFilesList(response.files);
        } else if (response.bonds) {
            setFilesList(response.bonds.map(b => ({ filename: b.bond_id, last_modified: b.last_modified })));
        } else {
            setFilesList([]);
        }
        setIsSearching(false);
    }


    return (<Col className={"col-12"}>
        <h4><FontAwesomeIcon icon={faCloudArrowDown} className="me-2"/>Restore</h4>
        <Row>
            <Col className={"col-12 col-xl-4"}>
                <InputGroup className="mb-3">
                    <InputGroup.Text className="backupInputLabel">Folder</InputGroup.Text>
                    <Form.Control
                        placeholder={bond.id}
                        ref={searchBackupRef}
                        initialvalue={bond.id}
                    />
                    <Button variant="primary" onClick={getBackupsList} disabled={isSearching || restoreStarted}>
                        {isSearching ? <FontAwesomeIcon icon={faSpinner} className="fa-spin"/> :
                            <FontAwesomeIcon icon={faMagnifyingGlass} className=""/>}

                    </Button>
                </InputGroup>
            </Col>
            <Col className={"col-12 col-xl-8"}>
                {restoreProgress !== 0 && <Row>
                    <Col className={"col-3 align-content-center"}>
                        <h4><Badge bg={"secondary"} className={"text-center pe-2 w-100"}>
                            {restoreStarted ? <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2"/> : ""}
                            {restoreStatus}
                        </Badge></h4>
                    </Col>
                    <Col className={"col-9"}>
                        <ProgressBar now={restoreProgress}
                                     animated={restoreStarted}
                                     variant={restoreProgress === -1 ? "danger" : "primary"}
                                     label={`${restoreProgress}%`}
                                     className={"m-1 mt-1 mb-1 h-50"}/>
                    </Col>
                </Row>}
            </Col>
        </Row>
        <Col className={"col-12 restoreCol"}>
            {filesList.length > 0 && <BootstrapTable keyField={"filename"}
                                                     data={filesList}
                                                     columns={backupListHeaders}
                                                     filter={filterFactory()}
                                                     defaultSorted={[{dataField: 'last_modified', order: 'desc'}]}
            />}
        </Col>
    </Col>);
}

export default Restore;
